import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Button,
  Badge,
  InputGroup,
  Modal,
  Alert,
  Spinner,
  Dropdown,
} from "react-bootstrap";
import { Copy, Check, Mail, Send, X, RefreshCw, Trash, Plus, Home as Building, Save, Folder, Edit2 } from "react-feather";
import { QRCodeCanvas } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../ThemeContext";
import ScanLinksTable from "./ScanLinksTable";
import './common.css'
import { apifetch } from '../services/interceptors';
import MODULES from "./ExternalRiskModules";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COMMON_PROVIDERS = ['gmail.com', 'outlook.com', 'hotmail.com', 'live.com', 'yahoo.com', 'protonmail.com'];

const statusMap = {
  unused: { text: "Unused", color: "success" },
  "just created": { text: "Unused", color: "success" },
  used: { text: "Used", color: "secondary" },
  revoked: { text: "Revoked", color: "danger" },
};

// Default email template for uncredential scans
const DEFAULT_EMAIL_TEMPLATE = {
  id: 'default',
  name: 'Default Template',
  subject: 'Security Scan for {target}',
  body: "Please use the one-time link below to start the security scan.\n\nThis link can be used only once and expires in 7 days.\n\nScan Details:\n• Target: {target}\n• Modules: {modules}\n• Link Type: One-time use\n• Expires: 7 days\n\nScan Link:\n{scanLink}\n\nThis is an automated security assessment. The link above can only be used once and will expire in 7 days."
};

export default function GenerateScanLink() {
  const { isDarkMode } = useTheme();
  const themeClass = isDarkMode ? "bg-dark text-light" : "bg-light text-dark";

  const [target, setTarget] = useState("");
  const [modules, setModules] = useState([]);
  const [instantStart, setInstantStart] = useState(false);
  const [expiresIn] = useState(604800);

  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkStatus, setLinkStatus] = useState("unused");

  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const token = localStorage.getItem("token");
  const [error, setError] = useState("");
  
  // Email template state
  const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL_TEMPLATE.body);
  const [emailSubject, setEmailSubject] = useState(DEFAULT_EMAIL_TEMPLATE.subject);
  
  // Email Template Management
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateBody, setTemplateBody] = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [templateError, setTemplateError] = useState("");
  const [templateMessage, setTemplateMessage] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: "", body: "" });
  
  // New organization creation states
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [organizationName, setOrganizationName] = useState("");
  const [website, setWebsite] = useState("");
  const [primaryPerson, setPrimaryPerson] = useState("");
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [orgError, setOrgError] = useState("");
  const [orgMessage, setOrgMessage] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);

  const toggleModule = (m) =>
    setModules((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));

  // Fetch email templates
  const fetchTemplates = async () => {
    try {
      const response = await apifetch("s2/api/email-templates", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Filter templates by type 'uncredential'
        const uncredentialTemplates = data.templates?.filter(t => t.template_type === 'uncredential') || [];
        setTemplates(uncredentialTemplates);
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
  };

  // Save/Update template
  const saveTemplate = async (e) => {
    e.preventDefault();
    setSavingTemplate(true);
    setTemplateError("");
    setTemplateMessage("");

    if (!templateName.trim()) {
        setTemplateError("Template name is required");
        setSavingTemplate(false);
        return;
    }

    try {
        const url = templateId 
            ? `s2/api/email-templates/${templateId}` 
            : "s2/api/email-templates";
        const method = templateId ? "PUT" : "POST";

        const response = await apifetch(url, {
            method: method,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                name: templateName,
                subject: templateSubject,
                body: templateBody,
                template_type: 'uncredential',
            }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
            setTemplateMessage(data.message || "Template saved successfully");
            
            // UPDATE FORM STATE DIRECTLY WITH PAYLOAD
            setEmailSubject(templateSubject);
            setEmailBody(templateBody);
            
            // If updating existing template, keep it selected
            if (templateId) {
                setSelectedTemplate(templateId);
            }
            
            // Update the templates list locally (no extra API call)
            if (templateId) {
                // Update existing template in the list
                setTemplates(prev => prev.map(t => 
                    t.id === templateId 
                        ? { ...t, name: templateName, subject: templateSubject, body: templateBody }
                        : t
                ));
            } else {
                // Add new template to the list
                const newTemplate = {
                    id: data.template?.id || 'temp_' + Date.now(),
                    name: templateName,
                    subject: templateSubject,
                    body: templateBody,
                    template_type: 'uncredential',
                    is_default: false
                };
                setTemplates(prev => [...prev, newTemplate]);
                setSelectedTemplate(newTemplate.id);
            }

            setTimeout(() => {
                setShowTemplateModal(false);
                setTemplateMessage("");
                resetTemplateForm();
            }, 1500);
        } else {
            setTemplateError(data.error || "Failed to save template");
        }
    } catch (error) {
        setTemplateError(`Error saving template: ${error.message}`);
    } finally {
        setSavingTemplate(false);
    }
};

  // Delete template
  const deleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      const response = await apifetch(`s2/api/email-templates/${templateToDelete}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await fetchTemplates();
        setShowDeleteConfirm(false);
        setTemplateToDelete(null);
      } else {
        setTemplateError(data.error || "Failed to delete template");
      }
    } catch (error) {
      setTemplateError(`Error deleting template: ${error.message}`);
    }
  };

  // Load template
  const loadTemplate = (template) => {
    setSelectedTemplate(template.id);
    setEmailSubject(template.subject || '');
    setEmailBody(template.body);
  };

  // Open template editor for new or existing
  const openTemplateEditor = (template = null) => {
    if (template) {
      setTemplateId(template.id);
      setTemplateName(template.name);
      setTemplateSubject(template.subject || '');
      setTemplateBody(template.body);
    } else {
      resetTemplateForm();
      setTemplateSubject(emailSubject);
      setTemplateBody(emailBody);
    }
    setTemplateError("");
    setTemplateMessage("");
    setShowTemplateModal(true);
  };

  const resetTemplateForm = () => {
    setTemplateId(null);
    setTemplateName("");
    setTemplateSubject("");
    setTemplateBody("");
    setTemplateError("");
    setTemplateMessage("");
  };

  const fetchOrganizations = async () => {
    try {
      const response = await apifetch("s1/api/organizations", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setError("Failed to fetch organizations");
        return;
      }
      const data = await response.json();
      setOrganizations(data.organizations);
      if (data.organizations.length > 0) {
        setSelectedOrganization(data.organizations[0].id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
    fetchTemplates();
  }, []);

  const createOrganization = async (e) => {
    e.preventDefault();
    setCreatingOrg(true);
    setOrgError("");
    setOrgMessage("");
    
    try {
      const response = await apifetch('s1/api/create_organization', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          primaryPerson,
          primaryEmail,
          primaryPhone,
          website,
          name: organizationName
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setOrgError(data.error || "Can't create organization");
      } else {
        await fetchOrganizations();
        if (data.organization && data.organization.id) {
          setSelectedOrganization(data.organization.id);
        }
        setOrgMessage('Organization created successfully');
        setOrganizationName('');
        setWebsite('');
        setPrimaryPerson('');
        setPrimaryEmail('');
        setPrimaryPhone('');
        setTimeout(() => {
          setShowOrgModal(false);
          setOrgMessage("");
        }, 1500);
      }
    } catch (error) {
      setOrgError(`Error creating organization: ${error.message}`);
    } finally {
      setCreatingOrg(false);
    }
  };

  const generateLink = async () => {
    if (!target || !modules.length) return;
    setLoading(true);

    try {
      const res = await apifetch("/s2/api/scan-links", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          target,
          targetType: "domain",
          modules,
          expiresIn,
          instantStart,
          oneTime: true,
          organizationId: selectedOrganization,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setLink(data.link);
        setLinkStatus("just created");
        updatePreview(data.link);
      }
    } finally {
      setLoading(false);
    }
  };

  const revokeLink = async () => {
    await apifetch("/s2/api/scan-links/revoke", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({ link }),
    });
    setLinkStatus("revoked");
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const validateEmail = (email) => {
    if (!EMAIL_REGEX.test(email)) {
      return "Invalid email format";
    }
    return null;
  };

  const addEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) return;

    const validationError = validateEmail(trimmedEmail);
    if (validationError) {
      setEmailError(validationError);
      return;
    }

    if (emails.includes(trimmedEmail)) {
      setEmailError("Email already added");
      return;
    }

    setEmails([...emails, trimmedEmail]);
    setEmailInput("");
    setEmailError("");
  };

  const handlePasteMultipleEmails = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const emailList = pastedText.split(/[,\n\s]+/).filter(email => email.trim());
    
    const validEmails = [];
    const invalidEmails = [];
    
    emailList.forEach(email => {
      const trimmedEmail = email.trim();
      if (EMAIL_REGEX.test(trimmedEmail) && !emails.includes(trimmedEmail)) {
        validEmails.push(trimmedEmail);
      } else if (trimmedEmail && !EMAIL_REGEX.test(trimmedEmail)) {
        invalidEmails.push(trimmedEmail);
      }
    });
    
    if (validEmails.length > 0) {
      setEmails([...emails, ...validEmails]);
    }
    
    if (invalidEmails.length > 0) {
      setEmailError(`${invalidEmails.length} invalid email(s) found: ${invalidEmails.slice(0, 3).join(', ')}${invalidEmails.length > 3 ? '...' : ''}`);
    } else {
      setEmailError("");
    }
  };

  const removeEmail = (e) => setEmails(emails.filter((x) => x !== e));

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addEmail();
    }
  };

  const updatePreview = (scanLink) => {
    const selectedModules = modules.map(m => MODULES.find(module => module.id === m)?.label).filter(Boolean);
    
    const subject = emailSubject
      .replace(/{target}/g, target || '[Domain]')
      .replace(/{modules}/g, selectedModules.join(', ') || 'All modules')
      .replace(/{scanLink}/g, scanLink || link || '[Link will appear here]');

    const body = emailBody
      .replace(/{target}/g, target || '[Domain]')
      .replace(/{modules}/g, selectedModules.join(', ') || 'All modules')
      .replace(/{scanLink}/g, scanLink || link || '[Link will appear here]');
    
    setPreviewContent({ subject, body });
  };

  useEffect(() => {
    if (link) {
      updatePreview(link);
    }
  }, [emailBody, emailSubject, modules, target, link]);

  const getEmailContent = () => {
    const selectedModules = modules.map(m => MODULES.find(module => module.id === m)?.label).filter(Boolean);
    
    const subject = emailSubject
      .replace(/{target}/g, target || '[Domain]')
      .replace(/{modules}/g, selectedModules.join(', ') || 'All modules')
      .replace(/{scanLink}/g, link || '[Link will appear here]');

    const body = emailBody
      .replace(/{target}/g, target || '[Domain]')
      .replace(/{modules}/g, selectedModules.join(', ') || 'All modules')
      .replace(/{scanLink}/g, link || '[Link will appear here]');

    return { subject, body };
  };

  const getMailToUrl = (provider) => {
    const emailList = emails.join(',');
    const { subject, body } = getEmailContent();
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    
    switch(provider) {
      case 'gmail':
        return `https://mail.google.com/mail/?view=cm&fs=1&to=${emailList}&su=${encodedSubject}&body=${encodedBody}`;
      case 'outlook':
        return `https://outlook.live.com/mail/0/deeplink/compose?to=${emailList}&subject=${encodedSubject}&body=${encodedBody}`;
      case 'outlook-office':
        return `https://outlook.office.com/mail/deeplink/compose?to=${emailList}&subject=${encodedSubject}&body=${encodedBody}`;
      case 'yahoo':
        return `https://mail.yahoo.com/mail/compose?to=${emailList}&subject=${encodedSubject}&body=${encodedBody}`;
      default:
        return `mailto:${emailList}?subject=${encodedSubject}&body=${encodedBody}`;
    }
  };

  const openEmailClient = (provider) => {
    if (emails.length === 0) {
      setEmailError("Please add at least one recipient email address.");
      return;
    }
    
    const url = getMailToUrl(provider);
    const emailWindow = window.open(url, "_blank");
    
    if (provider === 'outlook' || provider === 'outlook-office') {
      setTimeout(() => {
        if (emailWindow && emailWindow.closed === false) return;
        const { subject, body } = getEmailContent();
        window.location.href = `mailto:${emails.join(',')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      }, 2000);
    }
  };

  const allSelected = modules.length === MODULES.length;

  const toggleAllModules = () => {
    setModules(allSelected ? [] : MODULES.map(m => m.id));
  };

  const resetOrgForm = () => {
    setOrganizationName("");
    setWebsite("");
    setPrimaryPerson("");
    setPrimaryEmail("");
    setPrimaryPhone("");
    setOrgError("");
    setOrgMessage("");
  };

  return (
    <>
      <Card className={`shadow-lg border-0 ${themeClass} mb-4`}>
        <Card.Body className="p-4">
          <h5 className="fw-bold mb-4">Generate One-Time Scan Link</h5>

          <Form.Label style={{color: isDarkMode ? "white" : "black"}}>Domain or public IP addresses</Form.Label>
          <Form.Control
            className={"mb-3 "+(isDarkMode?"bg-dark text-light":"")}
            as="textarea"
            rows={3}
            maxLength={8192}
            aria-label="Domain or public IP addresses"
            placeholder="example.com or 8.8.8.8, 9.9.9.0/30"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
          <Form.Text className={isDarkMode ? 'text-light d-block mb-3' : 'text-muted d-block mb-3'}>Enter one domain, or comma-separated public IPv4 addresses, ranges or CIDRs. Up to 256 unique IPs.</Form.Text>
          
          <Form.Group controlId="organizationSelect" className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label style={{color: isDarkMode ? "white" : "black", margin: 0}}>
                Select Organization
              </Form.Label>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => {
                  resetOrgForm();
                  setShowOrgModal(true);
                }}
              >
                <Building size={14} className="me-1" />
                New Organization
              </Button>
            </div>
            <Form.Select
              value={selectedOrganization}
              onChange={(e) => setSelectedOrganization(e.target.value)}
              required
              className={isDarkMode ? 'bg-dark text-light placeholder-light ' : ''}
            > 
              {organizations.length === 0 && (
                <option value="" disabled>No organizations available. Click "New Organization" to create one.</option>
              )}
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Form.Select>
              <Form.Text className={isDarkMode ? 'text-light' : 'text-muted'}>Automatic IP discovery uses the saved organization's registered name, website and managed inventory. Choose the organization that owns the target domain. A registered company name is optional.</Form.Text>
            {organizations.length === 0 && (
              <Form.Text className="text-warning">
                You need to create an organization before generating a scan link.
              </Form.Text>
            )}
          </Form.Group>

          <p className="m-2">Select Modules:</p>
          <div className="d-flex gap-2 mb-3 flex-wrap">
            <Badge
              pill
              bg={allSelected ? "danger" : "success"}
              style={{ cursor: "pointer" }}
              onClick={toggleAllModules}
            >
              {allSelected ? "Unselect All" : "Select All"}
            </Badge>
            {MODULES.map((m) => (
              <Badge
                key={m.id}
                pill
                bg={modules.includes(m.id) ? "primary" : "secondary"}
                style={{ cursor: "pointer" }}
                onClick={() => toggleModule(m.id)}
              >
                {m.label}
              </Badge>
            ))}
          </div>

          <Button
            className="w-100 mb-3"
            disabled={!target || !modules.length || loading || selectedOrganization === "" || organizations.length === 0}
            onClick={generateLink}
          >
            {loading ? "Generating…" : "Generate Link"}
          </Button>

          <AnimatePresence>
            {link && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                <hr className="my-4" />

                <Badge bg={statusMap[linkStatus].color} className="mb-2">
                  {statusMap[linkStatus].text}
                </Badge>
 
                <InputGroup className={`${isDarkMode?"bg-dark":""} mb-3`}>
                  <Form.Control className={`${isDarkMode?"bg-dark":""}`} readOnly value={link} />
                  <Button onClick={copyLink}>
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </Button>
                  <Button variant="outline-danger" onClick={revokeLink}>
                    <Trash size={16} />
                  </Button>
                </InputGroup>

                <div className="text-center mb-3">
                  <QRCodeCanvas value={link} size={120} />
                </div>

                {/* Email Templates Section */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className={`${isDarkMode ? "text-light" : "text-muted"} text-uppercase mb-0`}>
                      <Mail size={16} className="me-2" />
                      Email Templates
                    </h6>
                    <div className="d-flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline-primary"
                        onClick={() => openTemplateEditor()}
                      >
                        <Plus size={14} className="me-1" />
                        New Template
                      </Button>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                          <Folder size={14} className="me-1" />
                          {selectedTemplate ? 'Template' : 'Load Template'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className={isDarkMode ? "bg-dark" : ""}>
                          <Dropdown.Item  
                            onClick={() => {
                              setSelectedTemplate(null);
                              setEmailSubject(DEFAULT_EMAIL_TEMPLATE.subject);
                              setEmailBody(DEFAULT_EMAIL_TEMPLATE.body);
                            }}
                            className={isDarkMode ? "text-light" : ""}
                          >
                            Default Template
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          {templates.map((template) => (
                            <Dropdown.Item 
                              key={template.id}
                              onClick={() => loadTemplate(template)}
                              className={`${isDarkMode ? "text-light" : ""} d-flex justify-content-between align-items-center`}
                            >
                              <span>{template.name}</span>
                              <div className="d-flex gap-1">
                                <Button 
                                  size="sm" 
                                  variant="outline-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openTemplateEditor(template);
                                  }}
                                >
                                  <Edit2 size={12} />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTemplateToDelete(template.id);
                                    setShowDeleteConfirm(true);
                                  }}
                                >
                                  <Trash size={12} />
                                </Button>
                              </div>
                            </Dropdown.Item>
                          ))}
                          {templates.length === 0 && (
                            <Dropdown.Item disabled>No saved templates</Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                  {selectedTemplate && (
                    <div className="small text-muted mb-2">
                      <Badge bg="info" className="me-1">Loaded</Badge>
                      {templates.find(t => t.id === selectedTemplate)?.name || 'Custom'}
                    </div>
                  )}
                </div>

                {/* Enhanced Email Input Section */}
                <h6 className={`${isDarkMode?"text-light":"text-muted"} text-uppercase mt-4`}>Add Recipients</h6>
                
                {/* Email chips display */}
                <div className="d-flex flex-wrap gap-2 p-2 border rounded mb-2" style={{ minHeight: '60px' }}>
                  {emails.map((e) => (
                    <Badge key={e} pill bg="primary" className="d-flex align-items-center gap-1 p-2">
                      <span>{e}</span>
                      <X size={12} style={{ cursor: 'pointer' }} onClick={() => removeEmail(e)} />
                    </Badge>
                  ))}
                  {emails.length === 0 && (
                    <span className="text-muted small">No emails added yet. Type or paste emails below.</span>
                  )}
                </div>

                {/* Email input with Add button */}
                <div className="mb-2">
                  <InputGroup>
                    <Form.Control
                      className={`${isDarkMode?"bg-dark text-light":""}`}
                      placeholder="Enter email address (e.g., user@example.com)"
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value);
                        setEmailError("");
                      }}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePasteMultipleEmails}
                      isInvalid={!!emailError}
                    />
                    <Button onClick={addEmail} variant="outline-primary">
                      <Plus size={16} /> Add
                    </Button>
                  </InputGroup>
                  {emailError && (
                    <Form.Text className="text-danger">
                      {emailError}
                    </Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    Tip: You can paste multiple emails separated by commas, spaces, or new lines
                  </Form.Text>
                </div>

                {/* Email subject and body editor */}
                <h6 className={`${isDarkMode?"text-light":"text-muted"} text-uppercase mt-3`}>Email Content</h6>
                <Form.Text className={`${isDarkMode ? "text-light" : "text-muted"} d-block mb-1`}>
                  Use {'{target}'}, {'{modules}'}, {'{scanLink}'} as placeholders
                </Form.Text>
                
                <Form.Group className="mb-2">
                  <Form.Label className={isDarkMode ? "text-light" : ""}>Subject</Form.Label>
                  <Form.Control
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className={`${isDarkMode ? "bg-dark text-light" : ""}`}
                    placeholder="Email subject line..."
                  />
                </Form.Group>

                <Form.Control
                  as="textarea"
                  rows={3}
                  value={emailBody} 
                  onChange={(e) => setEmailBody(e.target.value)}
                  className={`${isDarkMode?"bg-dark text-light":""} mb-2`}
                  placeholder="Customize your email message..."
                />

                {/* Action buttons */}
                <div className="d-flex gap-2 mb-3 flex-wrap">
                  <Button
                    variant="primary"
                    onClick={() => setShowPreview(true)}
                    disabled={!emails.length}
                  >
                    Preview Email
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={() => openEmailClient('gmail')}
                    disabled={!emails.length}
                  >
                    <Mail size={14} /> Gmail
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => openEmailClient('outlook')}
                    disabled={!emails.length}
                  >
                    <Send size={14} /> Outlook.com
                  </Button>
                  <Button
                    variant="outline-info"
                    onClick={() => openEmailClient('outlook-office')}
                    disabled={!emails.length}
                  >
                    <Send size={14} /> Microsoft 365
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => openEmailClient('yahoo')}
                    disabled={!emails.length}
                  >
                    <Mail size={14} /> Yahoo
                  </Button>
                  <Button
                    variant="outline-success"
                    onClick={() => {
                      if (selectedTemplate) {
                        const template = templates.find(t => t.id === selectedTemplate);
                        if (template) {
                          openTemplateEditor(template);
                        }
                      } else {
                        openTemplateEditor();
                      }
                    }}
                  >
                    <Save size={14} className="me-1" />
                    {selectedTemplate ? 'Update Template' : 'Save as Template'}
                  </Button>
                </div>

                <div className="text-muted small">
                  <strong>Supported providers:</strong> Gmail, Outlook.com, Hotmail, Live, Microsoft 365, Yahoo, and any custom domain (Exchange, etc.)
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Card.Body>

        {/* Organization Creation Modal */}
        <Modal 
          show={showOrgModal} 
          onHide={() => {
            setShowOrgModal(false);
            resetOrgForm();
          }} 
          centered 
          size="lg"
          data-bs-theme={isDarkMode ? "dark" : "light"}
          contentClassName={isDarkMode ? "bg-dark" : ""}
          className={`organization-modal ${isDarkMode ? "bg-dark" : "light"}`}
        >
          <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
            <Modal.Title className={isDarkMode ? "text-light" : ""}>
              <Building className="me-2" size={20} />
              Create New Organization
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={createOrganization}>
            <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""}>
              {orgError && (
                <Alert variant="danger" onClose={() => setOrgError("")} dismissible>
                  {orgError}
                </Alert>
              )}
              {orgMessage && (
                <Alert variant="success" onClose={() => setOrgMessage("")} dismissible>
                  {orgMessage}
                </Alert>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>Organization Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="e.g., Acme Inc."
                  required
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Website</Form.Label>
                <Form.Control
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://example.com"
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Primary Contact Person *</Form.Label>
                <Form.Control
                  type="text"
                  value={primaryPerson}
                  onChange={(e) => setPrimaryPerson(e.target.value)}
                  placeholder="Full name"
                  required
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Primary Email *</Form.Label>
                <Form.Control
                  type="email"
                  value={primaryEmail}
                  onChange={(e) => setPrimaryEmail(e.target.value)}
                  placeholder="contact@example.com"
                  required
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Primary Phone</Form.Label>
                <Form.Control
                  type="tel"
                  value={primaryPhone}
                  onChange={(e) => setPrimaryPhone(e.target.value)}
                  placeholder="+1 234 567 8900"
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
              <Button variant="secondary" onClick={() => {
                setShowOrgModal(false);
                resetOrgForm();
              }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={creatingOrg || !organizationName || !primaryPerson || !primaryEmail}
              >
                {creatingOrg ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  "Create Organization"
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Email Template Modal */}
        <Modal 
          show={showTemplateModal} 
          onHide={() => {
            setShowTemplateModal(false);
            resetTemplateForm();
          }} 
          centered 
          size="lg"
          data-bs-theme={isDarkMode ? "dark" : "light"}
          contentClassName={isDarkMode ? "bg-dark" : ""}
        >
          <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
            <Modal.Title className={isDarkMode ? "text-light" : ""}>
              <Save className="me-2" size={20} />
              {templateId ? 'Update' : 'Save'} Email Template
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={saveTemplate}>
            <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""}>
              {templateError && (
                <Alert variant="danger" onClose={() => setTemplateError("")} dismissible>
                  {templateError}
                </Alert>
              )}
              {templateMessage && (
                <Alert variant="success" onClose={() => setTemplateMessage("")} dismissible>
                  {templateMessage}
                </Alert>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>Template Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Standard Scan Email"
                  required
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Subject</Form.Label>
                <Form.Text className={`${isDarkMode ? "text-light" : "text-muted"} d-block mb-1`}>
                  Use {'{target}'}, {'{modules}'}, {'{scanLink}'} as placeholders
                </Form.Text>
                <Form.Control
                  type="text"
                  value={templateSubject}
                  onChange={(e) => setTemplateSubject(e.target.value)}
                  placeholder="Email subject line..."
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Body Content</Form.Label>
                <Form.Text className={`${isDarkMode ? "text-light" : "text-muted"} d-block mb-1`}>
                  Use {'{target}'}, {'{modules}'}, {'{scanLink}'} as placeholders
                </Form.Text>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={templateBody}
                  onChange={(e) => setTemplateBody(e.target.value)}
                  placeholder="Write your email template here..."
                  className={isDarkMode ? "bg-dark text-light" : ""}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
              <Button variant="secondary" onClick={() => {
                setShowTemplateModal(false);
                resetTemplateForm();
              }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={savingTemplate || !templateName.trim()}
              >
                {savingTemplate ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  templateId ? 'Update Template' : 'Save Template'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          show={showDeleteConfirm}
          onHide={() => {
            setShowDeleteConfirm(false);
            setTemplateToDelete(null);
          }}
          centered
          data-bs-theme={isDarkMode ? "dark" : "light"}
          contentClassName={isDarkMode ? "bg-dark" : ""}
        >
          <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
            <Modal.Title className={isDarkMode ? "text-light" : ""}>
              Delete Template
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""}>
            <p>Are you sure you want to delete this template? This action cannot be undone.</p>
          </Modal.Body>
          <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
            <Button variant="secondary" onClick={() => {
              setShowDeleteConfirm(false);
              setTemplateToDelete(null);
            }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={deleteTemplate}>
              <Trash size={14} className="me-1" />
              Delete
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Enhanced Preview Modal */}
        <Modal 
          show={showPreview} 
          onHide={() => setShowPreview(false)} 
          centered 
          size="lg"
          className={`preview-modal ${isDarkMode ? "bg-dark" : "light"}`}
          data-bs-theme={isDarkMode ? "dark" : "light"}
          style={{ padding: '20px' }}
        >
          <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
            <Modal.Title className={isDarkMode ? "text-light" : ""}>Email Preview</Modal.Title>
          </Modal.Header>
          <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""} style={{ padding: '10px' }}>
            {/* Recipients section */}
            <div className="mb-3">
              <strong>To:</strong>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {emails.map((email, idx) => (
                  <Badge key={idx} bg="primary" className="me-1">
                    {email}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Subject line */}
            <div className="mb-3">
              <strong>Subject:</strong>
              <div className="p-2 bg-light bg-opacity-10 rounded mt-1">
                {previewContent.subject}
              </div>
            </div>
            
            {/* Email body preview */}
            <div className="mb-3">
              <strong>Message Body:</strong>
              <div className="p-3 border rounded mt-1" style={{ whiteSpace: "pre-wrap", backgroundColor: isDarkMode ? "#2b2b2b" : "#f8f9fa", margin: '5px 0' }}>
                {previewContent.body}
              </div>
            </div>
            
            {/* Scan link section - prominently displayed */}
            {link && (
              <div className="mt-3 p-3 rounded" style={{ backgroundColor: isDarkMode ? "#1e3a5f" : "#e3f2fd", borderLeft: `4px solid ${isDarkMode ? "#64b5f6" : "#1976d2"}`, margin: '10px 0' }}>
                <strong className="d-block mb-2">🔗 Scan Link (One-time use):</strong>
                <div className="d-flex align-items-center gap-2">
                  <code style={{ wordBreak: "break-all", flex: 1 }}>{link}</code>
                  <Button size="sm" variant="outline-primary" onClick={copyLink}>
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </div>
                <div className="text-center mt-2">
                  <QRCodeCanvas value={link} size={80} />
                </div>
                <small className="text-muted d-block mt-2">
                  ⚠️ This link can only be used once and expires in 7 days
                </small>
              </div>
            )} 
            
            {/* Module summary */}
            {modules.length > 0 && (
              <div className="mt-3" style={{ margin: '10px 0' }}>
                <strong>Selected Modules:</strong>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {modules.map(m => {
                    const module = MODULES.find(mod => mod.id === m);
                    return module ? (
                      <Badge key={m} bg="info" className="me-1">
                        {module.label}
                      </Badge>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
            <Button variant="secondary" onClick={() => setShowPreview(false)}>
              Close
            </Button>
            <Button 
              variant="danger" 
              onClick={() => {
                setShowPreview(false);
                openEmailClient('gmail');
              }}
              disabled={!emails.length}
            >
              Send via Gmail
            </Button>
            <Button 
              variant="primary" 
              onClick={() => {
                setShowPreview(false);
                openEmailClient('outlook');
              }}
              disabled={!emails.length}
            >
              Send via Outlook.com
            </Button>
            <Button 
              variant="info" 
              onClick={() => {
                setShowPreview(false);
                openEmailClient('outlook-office');
              }}
              disabled={!emails.length}
            >
              Send via Microsoft 365
            </Button>
          </Modal.Footer>
        </Modal>
      </Card>
      <ScanLinksTable />
    </>
  );
}